import { InfragraphInfrastructure } from '../types/infrastructure';
import { parseInfrastructureContent } from './parseInfrastructureContent';
import { validateInfragraphFormat } from './validateInfragraphFormat';

export interface SampleTopology {
  id: string;
  name: string;
  description: string;
  filename: string;
  category: 'hierarchical' | 'ring' | 'dragonfly' | 'torus';
  complexity: 'simple' | 'medium' | 'complex';
  nodeCount: number;
}

export const AVAILABLE_SAMPLES: SampleTopology[] = [
  {
    id: 'spine-leaf',
    name: 'Two Tier',
    description: '32 GPUs in 2-tier Clos topology with 4 spine + 4 leaf switches',
    filename: 'sample-two-tier-infragraph.yaml',
    category: 'hierarchical',
    complexity: 'medium',
    nodeCount: 12
  },
  {
    id: 'three-tier-dual-plane',
    name: 'Three Tier',
    description: '3-tier dual-plane topology with scale-up and scale-out switches',
    filename: 'sample-three-tier-dual-plane-with-scale-up-infragraph.json',
    category: 'hierarchical',
    complexity: 'complex',
    nodeCount: 16
  },
  {
    id: 'ring-8node',
    name: 'Single Ring',
    description: 'Simple bidirectional ring with 8 switches and 16 hosts',
    filename: 'sample-single-ring-infragraph.yaml',
    category: 'ring',
    complexity: 'simple',
    nodeCount: 24
  },
  {
    id: 'ring-12node-dual',
    name: 'Dual Ring',
    description: 'Advanced counter-rotating dual rings with 24 dual-homed hosts',
    filename: 'sample-dual-ring-infragraph.yaml',
    category: 'ring',
    complexity: 'complex',
    nodeCount: 36
  },
  {
    id: 'dragonfly-3groups',
    name: 'DF-3×4×2',
    description: 'Simple 3-group dragonfly with 4 switches per group, all-to-all intra-group connectivity',
    filename: 'sample-simple-dragonfly-infragraph.json',
    category: 'dragonfly',
    complexity: 'medium',
    nodeCount: 36
  },
  {
    id: 'dragonfly-9groups',
    name: 'DF-9×4×2',
    description: 'True 9-group dragonfly with 4 switches per group, full mesh intra-group connectivity',
    filename: 'sample-large-dragonfly-infragraph.json',
    category: 'dragonfly',
    complexity: 'complex',
    nodeCount: 108
  },
  {
    id: 'tpu-v4-pod',
    name: 'TPU v4 Pod',
    description: 'Google TPU v4 Pod: 64 chips in 4×4×4 3D torus with ICI interconnect',
    filename: 'sample-tpu-v4-pod-infragraph.yaml',
    category: 'torus',
    complexity: 'complex',
    nodeCount: 16
  }
];

/**
 * Load a sample infragraph file from the public directory
 *
 * @param filename - Name of the file to load (must be in infragraph format)
 * @returns Parsed InfragraphInfrastructure data
 * @throws Error if file not found or not in valid infragraph format
 */
export async function loadSampleData(filename: string = 'sample-two-tier-infragraph.yaml'): Promise<InfragraphInfrastructure> {
  try {
    // Fetch the file from public directory
    const response = await fetch(`/${filename}`);
    if (!response.ok) {
      throw new Error(`Failed to load sample data: ${response.statusText}`);
    }

    const fileText = await response.text();
    const data = parseInfrastructureContent(fileText, filename);

    // Validate that it's in infragraph format
    validateInfragraphFormat(data, filename);

    return data as InfragraphInfrastructure;
  } catch (error) {
    console.error(`Error loading sample data from ${filename}:`, error);
    throw error;
  }
}

/**
 * Load a sample topology by ID
 *
 * @param topologyId - ID of the topology from AVAILABLE_SAMPLES
 * @returns Parsed InfragraphInfrastructure data
 */
export async function loadSampleTopology(topologyId: string): Promise<InfragraphInfrastructure> {
  const topology = AVAILABLE_SAMPLES.find(t => t.id === topologyId);
  if (!topology) {
    throw new Error(`Unknown topology: ${topologyId}`);
  }

  return loadSampleData(topology.filename);
}
